import SidebarTitle from "../components/SidebarTitle.tsx";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { MantineProvider, Menu } from "@mantine/core";
import "@mantine/core/styles.css";

import { clearDefaultMenuItem, fetchMenu, fetchMenuTitles, setDefaultMenuItem } from "../services/menu.ts";
import { getEmbedUrl } from "../services/embed.ts";
import { API_BASE } from "../services/apiBase.ts";
import {
  clearAdminPreviewContext,
  createSavedItem,
  getAdminPreviewContextKey,
  getAdminTenantHeader,
  listPersonalMenuAssignments,
  listSavedItems,
  type PersonalMenuAssignment,
  setAdminPreviewContext,
} from "../services/saved.ts";
import {
  applyRememberedParametersToUrl,
  extractParametersFromMessage,
  extractParametersFromObject,
  extractParameterArrayFromRequestBody,
  extractParametersFromRequestBody,
  extractParameterSlugMap,
  extractParameterKeys,
  extractParametersFromUrl,
  applyParameterPatch,
  filterToKnownParameters,
  mergeNonEmptyParameters,
  mapParameterKeys,
  mergeRememberedParameters,
  preferRecentFilterParameters,
  readFilterMemory,
  writeFilterMemory,
} from "../services/filterMemory.ts";
import {
  getFilterPreference,
  putFilterPreference,
} from "../services/filterPreferences.ts";
import { canEditMenuResource, editableSavedResourceKeys } from "../services/menuEditPolicy.ts";
import "../config/reporttool.css";

import { isDashboardCreationRequest, registerCreatedDashboard } from "../services/dashboardCreation";
import CreateReport from "./CreateReport.tsx";
import MyDataSavedBrowser from "./MyDataSavedBrowser.tsx";
import SdkDashboardView from "./SdkDashboardView.tsx";
import SdkErrorBoundary from "../components/SdkErrorBoundary.tsx";
import DevSdkOverlaySuppressor from "../components/DevSdkOverlaySuppressor.tsx";
import {
  InteractiveQuestion,
  StaticQuestion,
} from "@metabase/embedding-sdk-react";
import CreateDashboard from "./CreateDashboard.tsx";
import ReportAdministration from "./ReportAdministration.tsx";
import {
  getAdministrationContext,
  getReportPreview,
  type AdministrationContext,
  type ReportPreview,
} from "../services/reportAdministration.ts";
import { useLocation, useNavigate } from "react-router-dom";
import "../types/metabase-webcomponents.d.ts";

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const MB_INSTANCE =
  process.env.REACT_APP_METABASE_PROXY_BASE ||
  `${window.location.origin}/api/metabase`;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
function readCssVar(el: Element, name: string) {
  return getComputedStyle(el).getPropertyValue(name).trim();
}

function buildMetabaseThemeFromCss(
  el: Element,
  themeMode: "dark" | "light",
) {
  const bgMain = readCssVar(el, "--bg-main");
  const bgPanel = readCssVar(el, "--bg-panel");
  const text = readCssVar(el, "--text");
  const textDim = readCssVar(el, "--text-dim");
  const primary = readCssVar(el, "--primary");
  const divider = readCssVar(el, "--divider");
  const activeAccent = readCssVar(el, "--active-accent");
  const hover = readCssVar(el, "--hover");

  return {
    base: themeMode === "dark" ? "dark" : "light",
    colors: {
      brand: primary,
      background: bgMain,
      "background-secondary": bgPanel,
      "background-hover": hover,
      border: divider,
      "brand-hover": activeAccent || primary,
      "text-primary": text,
      "text-secondary": textDim,
      "text-tertiary": textDim,
    },
  };
}

function getUserEmailFromAuth(auth: any) {
  return auth.user?.profile?.email || null;
}

type MenuItem = {
  key: string;
  label: string;
  default?: boolean;
  section?: string;
  slug?: string;
  resource: any;
};

type EmbedParams = {
  initialParameters?: Record<string, any>;
  parameters?: Record<string, any>;
  hiddenParameters?: string[];
  requiredParameters?: string[];
  allowedValues?: Record<string, Array<string | number | boolean>>;
};

type MenuGroup = {
  title: string;
  items: MenuItem[];
};

function decodeJwtPayload(token: string) {
  const payload = token.split(".")[1];
  const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json);
}

function rewriteMetabaseUrlToProxy(url: string) {
  const u = new URL(url);

  const isMetabasePublicPath =
    u.pathname.startsWith("/public/") ||
    u.pathname.startsWith("/embed/") ||
    u.pathname.startsWith("/app/");

  if (!isMetabasePublicPath) return url;

  return `${MB_INSTANCE}${u.pathname}${u.search}${u.hash}`;
}

function getEmbedResourceFromUrl(urlString: string) {
  const u = new URL(urlString);
  const parts = u.pathname.split("/").filter(Boolean);
  const idx = parts.indexOf("embed");
  if (idx === -1) return null;

  const kind = parts[idx + 1];
  const jwt = parts[idx + 2];
  if (!jwt) return null;

  const payload = decodeJwtPayload(jwt);
  const res = payload?.resource || {};
  return {
    kind,
    payload,
    questionId: res.question,
    dashboardId: res.dashboard,
  };
}

function getPublicDashboardUuid(urlString: string) {
  try {
    const url = new URL(urlString, window.location.origin);
    return url.pathname.match(/\/public\/dashboard\/([0-9a-f-]{36})(?:\/|$)/i)?.[1]?.toLowerCase() || null;
  } catch {
    return null;
  }
}

function getClickthroughHiddenParameters(urlString: string, parameterNames: string[]) {
  try {
    const [baseAndQuery, hash] = urlString.split("#");
    const url = new URL(baseAndQuery, window.location.origin);
    const values = [
      ...url.searchParams.getAll("hide_parameters"),
      ...(hash ? new URLSearchParams(hash).getAll("hide_parameters") : []),
    ];
    const requested = values.flatMap(value => value.split(",")).map(value => value.trim()).filter(Boolean);
    if (!requested.length || requested.includes("all")) return parameterNames;
    return [...new Set(requested)];
  } catch {
    return parameterNames;
  }
}

async function fetchMetabaseName(
  kind: "question" | "dashboard",
  id: number,
  accessToken: string,
  email?: string | null,
) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  if (email) headers["X-User-Email"] = email;

  const r = await fetch(
    `${API_BASE}/api/metabase-name?kind=${encodeURIComponent(kind)}&id=${id}`,
    { headers },
  );

  if (!r.ok) return null;

  const data = await r.json();
  return data?.name || null;
}

function applyInitialParametersToUrl(
  url: string,
  initialParameters?: Record<string, any>,
) {
  if (!url) return url;
  if (!initialParameters || typeof initialParameters !== "object") return url;

  const [baseAndQuery, hash] = url.split("#");
  const u = new URL(baseAndQuery);

  for (const [key, raw] of Object.entries(initialParameters)) {
    if (raw == null) continue;

    if (Array.isArray(raw)) {
      if (u.searchParams.has(key)) continue;
      for (const v of raw) {
        if (v == null) continue;
        u.searchParams.append(key, String(v));
      }
      continue;
    }

    if (typeof raw === "object") {
      if (!u.searchParams.has(key)) {
        u.searchParams.set(key, JSON.stringify(raw));
      }
      continue;
    }

    u.searchParams.set(key, String(raw));
  }

  return hash ? `${u.toString()}#${hash}` : u.toString();
}

function getFilterMemoryResourceKey(item: MenuItem | null, mode: string) {
  const resource = item?.resource || {};

  if (typeof resource.entityId === "number") {
    return `${resource.kind || mode}:${resource.entityId}`;
  }

  if (typeof resource.url === "string") {
    try {
      const url = new URL(resource.url, window.location.origin);
      return `view:${url.pathname}`;
    } catch {
      return `view:${resource.url}`;
    }
  }

  return item?.key ? `${mode}:${item.key}` : null;
}

function applyThemeToUrl(url: string, themeMode: "dark" | "light") {
  if (!url) return url;

  const themeValue = themeMode === "dark" ? "night" : "default";

  const [baseAndQuery, hash] = url.split("#");
  const u = new URL(baseAndQuery);

  u.searchParams.set("theme", themeValue);

  const hp = new URLSearchParams(hash || "");
  hp.set("theme", themeValue);

  return `${u.toString()}#${hp.toString()}`;
}

function loadMetabaseEmbedJs(instanceUrl: string) {
  return new Promise<void>((resolve, reject) => {
    if (document.querySelector('script[data-mb-embedjs="1"]')) return resolve();

    const s = document.createElement("script");
    s.defer = true;
    s.src = `${instanceUrl.replace(/\/$/, "")}/app/embed.js`;
    s.dataset.mbEmbedjs = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load embed.js"));
    document.head.appendChild(s);
  });
}

function defineMetabaseConfig(config: any) {
  (window as any).metabaseConfig = config;
}

function getMetabaseSdkStore() {
  const providerStore = (window as any).METABASE_PROVIDER_PROPS_STORE
    ?.getState?.()
    ?.internalProps
    ?.reduxStore;

  return (
    providerStore ||
    (window as any).METABASE_EMBEDDING_SDK_BUNDLE?.getSdkStore?.() ||
    null
  );
}

function applyEmbedOptions(
  url: string,
  { refreshSec = 60 }: { refreshSec?: number } = {},
) {
  if (!url) return url;

  const [baseAndQuery, hash] = url.split("#");
  const u = new URL(baseAndQuery);

  const hashParams = new URLSearchParams(hash || "");
  if (refreshSec && refreshSec > 0) {
    hashParams.set("refresh", String(refreshSec));
  }

  return `${u.toString()}#${hashParams.toString()}`;
}

function EmbedJsDashboard({
  apiBase,
  accessToken,
  email,
  instanceUrl,
  dashboardId,
  themeMode,
  themeHostEl,
  refreshSec,
  initialParameters,
}: {
  apiBase: string;
  accessToken: string;
  email: string | null;
  instanceUrl: string;
  dashboardId: number;
  themeMode: "dark" | "light";
  themeHostEl: Element | null;
  refreshSec?: number | null;
  initialParameters?: Record<string, any>;
}) {
  const [ready, setReady] = useState(false);
  const [token, setToken] = useState<string>("");
  const embedRef = useRef<any>(null);

  const theme = useMemo(
    () =>
      themeHostEl != null
        ? buildMetabaseThemeFromCss(themeHostEl, themeMode)
        : { base: themeMode === "dark" ? "dark" : "light" },
    [themeHostEl, themeMode],
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      defineMetabaseConfig({
        instanceUrl,
        isGuest: true,
        theme,
      });

      await loadMetabaseEmbedJs(instanceUrl);

      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
      };
      if (email) headers["X-User-Email"] = email;

      const r = await fetch(
        `${apiBase}/api/embedjs-token?dashboard_id=${dashboardId}`,
        { headers },
      );
      if (!r.ok) throw new Error("Failed to get embedjs token");

      const data = await r.json();

      if (!cancelled) {
        setToken(data.token);
        setReady(true);
      }
    })().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [apiBase, accessToken, email, instanceUrl, dashboardId, theme]);

  useEffect(() => {
    const el = embedRef.current;
    if (!el || !ready) return;

    if (typeof refreshSec === "number" && refreshSec > 0) {
      el.autoRefreshInterval = refreshSec;
      el.setAttribute("auto-refresh-interval", String(refreshSec));
    } else {
      el.autoRefreshInterval = undefined;
      el.removeAttribute("auto-refresh-interval");
    }

    if (initialParameters) {
      el.initialParameters = initialParameters;
      el.setAttribute("initial-parameters", JSON.stringify(initialParameters));
    } else {
      el.initialParameters = undefined;
      el.removeAttribute("initial-parameters");
    }

    console.log("[embedjs] refreshSec", refreshSec);
    console.log("[embedjs] initialParameters", initialParameters);
  }, [refreshSec, initialParameters, ready, token]);

  if (!ready) return <div style={{ padding: 16 }}>Loading dashboard…</div>;

  return (
    <metabase-dashboard
      ref={embedRef}
      key={`${dashboardId}-${themeMode}-${token}`}
      token={token}
      with-title="false"
      with-downloads="true"
      drills="true"
      auto-refresh-interval={refreshSec ? String(refreshSec) : undefined}
    />
  );
}

type DashboardRenderMode = "embedjs" | "sdk" | "view";

function normalizeDashboardRenderMode(value: unknown): DashboardRenderMode | null {
  if (typeof value !== "string") return null;

  const mode = value.trim().toLowerCase();

  if (["ea", "embedjs", "embed-js", "embedded-analytics"].includes(mode)) {
    return "embedjs";
  }

  if (["sdk", "metabase-sdk", "embedding-sdk"].includes(mode)) {
    return "sdk";
  }

  if (["view", "public", "public-url", "iframe"].includes(mode)) {
    return "view";
  }

  return null;
}

function slugHasRenderMode(value: unknown, mode: DashboardRenderMode) {
  if (typeof value !== "string") return false;

  const slug = value.trim().toLowerCase();
  if (!slug) return false;

  const aliases =
    mode === "embedjs"
      ? ["ea", "embedjs", "embed-js"]
      : mode === "sdk"
        ? ["sdk", "metabase-sdk", "embedding-sdk"]
        : ["view", "public", "public-url", "iframe"];

  return aliases.some(
    (alias) =>
      slug === alias ||
      slug.startsWith(`${alias}:`) ||
      slug.startsWith(`${alias}-`) ||
      slug.startsWith(`${alias}_`) ||
      slug.endsWith(`:${alias}`) ||
      slug.endsWith(`-${alias}`) ||
      slug.endsWith(`_${alias}`),
  );
}

function getDashboardRenderMode(it: MenuItem, resource: any): DashboardRenderMode | null {
  const explicitMode =
    normalizeDashboardRenderMode(resource.renderMode) ||
    normalizeDashboardRenderMode(resource.embedMode) ||
    normalizeDashboardRenderMode(resource.viewMode);

  if (explicitMode) return explicitMode;

  if (resource.useEmbedJs === true) return "embedjs";
  if (resource.useSdk === true || resource.sdk === true) return "sdk";
  if (resource.usePublicUrl === true) return "view";

  if (slugHasRenderMode(it.key, "embedjs") || slugHasRenderMode(it.slug, "embedjs") || slugHasRenderMode(resource.slug, "embedjs")) {
    return "embedjs";
  }

  if (slugHasRenderMode(it.key, "sdk") || slugHasRenderMode(it.slug, "sdk") || slugHasRenderMode(resource.slug, "sdk")) {
    return "sdk";
  }

  if (slugHasRenderMode(it.key, "view") || slugHasRenderMode(it.slug, "view") || slugHasRenderMode(resource.slug, "view")) {
    return "view";
  }

  return null;
}

function normalizeMenuItem(
  it: MenuItem,
  isCompanyAdministrator: boolean,
  editableSavedResources: ReadonlySet<string>,
): MenuItem {
  const r = it.resource || {};
  const mbType = r.mbType;
  const id = r.resourceId;
  const canEdit = canEditMenuResource(mbType, id, isCompanyAdministrator, editableSavedResources);
  const reportConfiguration = {
    reportId: r.reportId ?? r.report_id ?? null,
    airportRequired: r.airportRequired ?? r.airport_required ?? true,
  };

  if (mbType === "dashboard" && typeof id === "number") {
    if (canEdit) {
      return {
        ...it,
        resource: {
          kind: "sdk-dashboard",
          entityId: id,
          readOnly: false,
          canEdit: true,
          refreshSec: r.refreshSec ?? null,
          ...reportConfiguration,
        },
      };
    }

    const renderMode = getDashboardRenderMode(it, r);

    if (renderMode === "embedjs") {
      return {
        ...it,
        resource: {
          kind: "embedjs-dashboard",
          entityId: id,
          refreshSec: r.refreshSec ?? null,
          ...reportConfiguration,
        },
      };
    }

    if (renderMode === "sdk") {
      return {
        ...it,
        resource: {
          kind: "sdk-dashboard",
          entityId: id,
          readOnly: !canEdit,
          canEdit,
          refreshSec: r.refreshSec ?? null,
          ...reportConfiguration,
        },
      };
    }

    if (r.publicUrl) {
      return {
        ...it,
        resource: {
          kind: "view",
          entityId: id,
          refreshSec: r.refreshSec ?? null,
          url: r.publicUrl,
          ...reportConfiguration,
        },
      };
    }

    return {
      ...it,
      resource: {
        kind: "sdk-dashboard",
        entityId: id,
        readOnly: !canEdit,
        canEdit,
        refreshSec: r.refreshSec ?? null,
        ...reportConfiguration,
      },
    };
  }

  if (typeof id === "number") {
    return {
      ...it,
      resource: {
        kind: "sdk-question",
        entityId: id,
        readOnly: !canEdit,
        ...reportConfiguration,
      },
    };
  }

  return it;
}

function mergePersonalSelectionsIntoGroups(groups: MenuGroup[], selections: PersonalMenuAssignment[]): MenuGroup[] {
  const next: MenuGroup[] = (groups || []).map((group: MenuGroup) => ({
    ...group,
    items: [...(group.items || [])],
  }));

  for (const selection of selections) {
    const section = String(selection.menu_section || "").trim().toUpperCase();
    if (!section) continue;

    // A private choice moves this user's own menu location without
    // changing the tenant-role menu that everyone else with that role sees.
    for (const group of next) {
      group.items = group.items.filter((item) => {
        const resource = item.resource || {};
        return !(resource.mbType === selection.kind && resource.resourceId === selection.metabase_id);
      });
    }
    const item: MenuItem = {
      key: `saved:${selection.kind}:${selection.saved_item_id}`,
      label: selection.title,
      resource: { mbType: selection.kind, resourceId: selection.metabase_id, canEdit: true },
    };
    const existing = next.find(group => group.title.trim().toUpperCase() === section);
    if (existing) existing.items.push(item);
    else next.push({ title: section, items: [item] });
  }

  return next.filter(group => group.items.length || ["MY DATA", "CREATE", "ADMIN"].includes(group.title.toUpperCase()));
}

const ReportToolSidebar = memo(function ReportToolSidebar({
  collapsed,
  menuQuery,
  filteredMenu,
  activeItemKey,
  onMenuQueryChange,
  onSelectItem,
  canCreateSql,
  onSelectCreateReportSource,
}: {
  collapsed: boolean;
  menuQuery: string;
  filteredMenu: MenuGroup[];
  activeItemKey?: string;
  onMenuQueryChange: (value: string) => void;
  onSelectItem: (groupTitle: string, item: MenuItem) => void;
  canCreateSql?: boolean;
  onSelectCreateReportSource: (sourceMode: "model" | "sql") => void;
}) {
  return (
    <aside className="rt-left" aria-hidden={collapsed ? "true" : undefined}>
      <div className="rt-nav-top">
        <input
          className="rt-search"
          placeholder="Search dashboards & reports..."
          value={menuQuery}
          onChange={(e) => onMenuQueryChange(e.target.value)}
        />
      </div>

      <nav className="rt-nav">
        {filteredMenu.map((group) => (
          <details key={group.title} open>
            <summary className="rt-group-title">{group.title}</summary>
            <div className="rt-items">
              {(group.items || []).map((it) => {
                const isActive = activeItemKey === it.key;

                if (it.key === "create-report" && canCreateSql) {
                  return (
                    <Menu key={it.key} shadow="md" position="right-start" withinPortal>
                      <Menu.Target>
                        <button
                          className={`rt-item ${isActive ? "is-active" : ""}`}
                          aria-current={isActive ? "page" : undefined}
                          aria-label={it.label}
                          title={it.label}
                        >
                          <SidebarTitle title={it.label} />
                        </button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => onSelectCreateReportSource("model")}>
                          From Model
                        </Menu.Item>
                        <Menu.Item onClick={() => onSelectCreateReportSource("sql")}>
                          From SQL
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  );
                }

                return (
                  <button
                    key={it.key}
                    className={`rt-item ${isActive ? "is-active" : ""}`}
                    onClick={() => onSelectItem(group.title, it)}
                    aria-current={isActive ? "page" : undefined}
                    aria-label={it.label}
                    title={it.label}
                  >
                    <SidebarTitle title={it.label} />
                  </button>
                );
              })}
            </div>
          </details>
        ))}
      </nav>
    </aside>
  );
});

export default function ReportTool({
  logoSrc,
  themeMode,
  setThemeMode,
  renderWithMetabase,
}: {
  logoSrc?: string;
  themeMode: "dark" | "light";
  setThemeMode: (v: "dark" | "light") => void;
  renderWithMetabase: (
    purpose: "runtime" | "creator",
    content: React.ReactNode,
  ) => React.ReactNode;
}) {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const email = getUserEmailFromAuth(auth);
  const [questionSaveError, setQuestionSaveError] = useState<string | null>(null);
  const handleSavedQuestionSave = useCallback(
    async (
      question: { id: number; name?: string },
      context: { isNewQuestion: boolean },
    ) => {
      if (!context.isNewQuestion) return;
      setQuestionSaveError(null);
      try {
        if (!accessToken) throw new Error("Missing access token; please sign in again.");
        if (!Number.isInteger(question.id) || question.id <= 0) {
          throw new Error("Saved question is missing a valid Metabase id.");
        }
        const savedItem = await createSavedItem(
          accessToken,
          { kind: "card", metabase_id: question.id, title: question.name || "Untitled report" },
          email ?? undefined,
        );
        setActiveItem({
          key: `saved-card-${savedItem.id}-${question.id}`,
          label: question.name || "Untitled report",
          resource: {
            kind: "sdk-question",
            entityId: question.id,
            readOnly: false,
          },
        });
      } catch (error: any) {
        setQuestionSaveError(
          "The question was saved in Metabase, but could not be added to Saved Reports/Dashboards. " +
          (error?.message || "Please try saving it again."),
        );
      }
    },
    [accessToken, email],
  );
  const filterMemoryUserKey =
    email || auth.user?.profile?.sub?.toString() || "anonymous";

  const shellRef = useRef<HTMLDivElement | null>(null);

  const [availableMenuTitles, setAvailableMenuTitles] = useState<string[]>([]);
  const [menu, setMenu] = useState<MenuGroup[]>([]);
  const [menuReloadTick, setMenuReloadTick] = useState(0);
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [administrationContext, setAdministrationContext] =
    useState<AdministrationContext | null>(null);
  const [administrationContextError, setAdministrationContextError] =
    useState<string | null>(null);
  const [previewContext, setPreviewContext] = useState<ReportPreview | null>(null);

  const [iframeSrc, setIframeSrc] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const [menuQuery, setMenuQuery] = useState("");

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const lastDefaultMenuItemKeyRef = useRef<string | null>(null);
  const openDefaultAfterMenuReloadRef = useRef(false);
  const menuTargetKeyAfterReloadRef = useRef<string | null>(null);
  const parameterSlugMapRef = useRef<Record<string, string>>({});
  const knownParameterKeysRef = useRef<Set<string>>(new Set());
  const filterPreferenceSaveTimersRef = useRef(new Map<string, { timer: number; save: () => void }>());

  const resource = activeItem?.resource || {};
  const [reloadTick, setReloadTick] = useState(0);
  const columnMetadataScope = `${previewContext?.tenant.id ?? ""}:${previewContext?.role.id ?? ""}`;
  const [columnMetadata, setColumnMetadata] = useState<{ id: number; token: string; email: string | undefined; scope: string; dashboard: unknown } | null>(null);
  const clickthroughColumnMetadataRef = useRef<{
    uuid: string;
    id: number;
    token: string;
    email: string | undefined;
    scope: string;
  } | null>(null);

  const refreshSec = useMemo(() => {
    return typeof resource.refreshSec === "number" ? resource.refreshSec : null;
  }, [resource.refreshSec]);

  const readOnly = Boolean(resource.readOnly);

  const mode:
    | "view"
    | "saved"
    | "create-report"
    | "create-dashboard"
    | "report-administration"
    | "sdk-question"
    | "sdk-dashboard"
    | "ea-dashboard"
    | "embedjs-dashboard" =
    resource.kind === "create-report"
      ? "create-report"
      : resource.kind === "create-dashboard"
        ? "create-dashboard"
        : resource.kind === "report-administration"
          ? "report-administration"
        : resource.kind === "saved"
          ? "saved"
          : resource.kind === "sdk-question"
            ? "sdk-question"
            : resource.kind === "sdk-dashboard"
              ? "sdk-dashboard"
              : resource.kind === "ea-dashboard"
                ? "ea-dashboard"
                : resource.kind === "embedjs-dashboard"
                  ? "embedjs-dashboard"
                  : "view";

  const isEditorMode =
    mode === "create-report" ||
    mode === "create-dashboard" ||
    mode === "report-administration" ||
    mode === "sdk-question" ||
    mode === "sdk-dashboard";

  const shellClassName = [
    "rt-shell",
    "rt-theme",
    themeMode === "light" && "is-light",
    collapsed && "is-collapsed",
    isEditorMode && "is-editor",
  ]
    .filter(Boolean)
    .join(" ");

  const iframeKey = useMemo(() => activeItem?.key || "no-item", [activeItem]);
  const activeResource = activeItem?.resource;
  const activeItemKey = activeItem?.key;

  const entityIdNum: number | null =
    typeof resource.entityId === "number" && Number.isInteger(resource.entityId)
      ? resource.entityId
      : null;

  const location = useLocation();
  const nav = useNavigate();

  const filteredMenu = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();

    const previewMenu = previewContext?.menu || menu;
    const menuWithAdministration = administrationContext?.is_company_administrator
      ? [
          ...previewMenu,
          {
            title: "ADMIN",
            items: [
              {
                key: "report-administration",
                label: "Report Administration",
                resource: { kind: "report-administration" },
              },
            ],
          },
        ]
      : previewMenu;

    return !q
      ? menuWithAdministration
      : menuWithAdministration
          .map((g) => ({
            ...g,
            items: (g.items || []).filter((it) =>
              (it.label || "").toLowerCase().includes(q),
            ),
          }))
          .filter((g) => (g.items || []).length > 0);
  }, [administrationContext?.is_company_administrator, menu, menuQuery, previewContext]);

  const savedMenuTitles = availableMenuTitles;

  const defaultMenuItemKey = useMemo(() => {
    const defaultItem = menu
      .flatMap((group) => group.items || [])
      .find((item) => item.default);

    return defaultItem?.key || null;
  }, [menu]);

  const [mbEmbedParams, setMbEmbedParams] = useState<EmbedParams | null>(null);
  const [rememberedParameterState, setRememberedParameterState] = useState<{
    userKey: string;
    resourceKey: string;
    parameters: Record<string, any>;
  } | null>(null);
  const filterMemoryStorageUserKey = previewContext
    ? `${filterMemoryUserKey}:preview:${previewContext.tenant.id}:${previewContext.role.id}`
    : filterMemoryUserKey;

  const filterMemoryResourceKey = useMemo(
    () => getFilterMemoryResourceKey(activeItem, mode),
    [activeItem, mode],
  );
  const filterMemoryIdentity = filterMemoryResourceKey
    ? `${filterMemoryStorageUserKey}:${filterMemoryResourceKey}`
    : null;
  const [loadedFilterMemoryIdentity, setLoadedFilterMemoryIdentity] = useState<string | null>(null);
  const filterPreferenceReady =
    !filterMemoryIdentity || Boolean(previewContext) ||
    loadedFilterMemoryIdentity === filterMemoryIdentity;

  const rememberedParameters = useMemo(() => {
    if (!filterMemoryResourceKey) return undefined;
    if (
      rememberedParameterState?.userKey === filterMemoryStorageUserKey &&
      rememberedParameterState.resourceKey === filterMemoryResourceKey
    ) return rememberedParameterState.parameters;
    return readFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey)?.parameters;
  }, [filterMemoryResourceKey, filterMemoryStorageUserKey, rememberedParameterState]);

  const mergedInitialParameters = useMemo(
    () => {
      const merged = mergeRememberedParameters(
        rememberedParameters,
        mbEmbedParams?.initialParameters,
        {
          hiddenParameters: mbEmbedParams?.hiddenParameters,
          allowedValues: mbEmbedParams?.allowedValues,
          requiredParameters: mbEmbedParams?.requiredParameters,
        },
      );
      return mergeRememberedParameters(resource.initialParameters, merged, {
        hiddenParameters: mbEmbedParams?.hiddenParameters,
        allowedValues: mbEmbedParams?.allowedValues,
        requiredParameters: mbEmbedParams?.requiredParameters,
      });
    },
    [
      rememberedParameters,
      mbEmbedParams?.initialParameters,
      mbEmbedParams?.hiddenParameters,
      mbEmbedParams?.allowedValues,
      mbEmbedParams?.requiredParameters,
      resource.initialParameters,
    ],
  );

  const mergedEmbedParams = useMemo(
    () => ({
      ...mbEmbedParams,
      initialParameters: mergedInitialParameters,
      parameters: mergedInitialParameters,
      hiddenParameters: [...new Set([
        ...(mbEmbedParams?.hiddenParameters || []),
        ...(Array.isArray(resource.hiddenParameters) ? resource.hiddenParameters : []),
      ])],
    }),
    [mbEmbedParams, mergedInitialParameters, resource.hiddenParameters],
  );

  const persistActiveParameters = useCallback(
    (parameters?: Record<string, any>, options?: { clearEmpty?: boolean; trusted?: boolean }) => {
      if (!filterMemoryResourceKey || !parameters || !Object.keys(parameters).length) {
        return;
      }
      if (
        ["sdk-dashboard", "embedjs-dashboard"].includes(mode) &&
        knownParameterKeysRef.current.size === 0 &&
        !options?.trusted
      ) {
        return;
      }

      const mappedParameters = filterToKnownParameters(
        mapParameterKeys(parameters, parameterSlugMapRef.current),
        options?.trusted ? undefined : knownParameterKeysRef.current,
        {
          dropEmpty:
            ["sdk-dashboard", "embedjs-dashboard"].includes(mode) &&
            !options?.clearEmpty,
        },
      );
      if (!mappedParameters) return;

      const currentParameters =
        readFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey)?.parameters;
      const merged = mergeRememberedParameters(
        applyParameterPatch(currentParameters, mappedParameters, {
          clearEmpty: options?.clearEmpty,
        }),
        mbEmbedParams?.initialParameters,
        {
          hiddenParameters: mbEmbedParams?.hiddenParameters,
          allowedValues: mbEmbedParams?.allowedValues,
          requiredParameters: mbEmbedParams?.requiredParameters,
        },
      );

      // Public/linked iframe views are sampled periodically. Avoid rewriting
      // the same preference on every sample (and on repeated SDK events).
      if (JSON.stringify(currentParameters || {}) === JSON.stringify(merged || {})) {
        return;
      }

      writeFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey, {
        parameters: merged,
      });
      setRememberedParameterState({
        userKey: filterMemoryStorageUserKey,
        resourceKey: filterMemoryResourceKey,
        parameters: merged,
      });

      // A preview keeps its own local selections, scoped to the target tenant
      // and role. The server preference endpoint belongs to the actual user.
      if (accessToken && !previewContext) {
        const saveKey = `${filterMemoryStorageUserKey}:${filterMemoryResourceKey}`;
        const pending = filterPreferenceSaveTimersRef.current.get(saveKey);
        if (pending) window.clearTimeout(pending.timer);
        const save = () => {
          filterPreferenceSaveTimersRef.current.delete(saveKey);
          putFilterPreference(accessToken, filterMemoryResourceKey, merged, email)
            .catch(console.error);
        };
        const timer = window.setTimeout(save, 400);
        filterPreferenceSaveTimersRef.current.set(saveKey, { timer, save });
      }
    },
    [
      accessToken,
      email,
      filterMemoryResourceKey,
      filterMemoryStorageUserKey,
      mbEmbedParams?.initialParameters,
      mbEmbedParams?.hiddenParameters,
      mbEmbedParams?.allowedValues,
      mbEmbedParams?.requiredParameters,
      mode,
      previewContext,
    ],
  );

  // Keep the dashboard SDK options stable during its automatic refresh.
  // Recreating this callback on a parent render can make the SDK treat the
  // options as a new configuration and briefly replace the preview content.
  const handleSdkParametersChange = useCallback(
    (parameters: Record<string, any>) => {
      persistActiveParameters(parameters, { clearEmpty: true, trusted: true });
    },
    [persistActiveParameters],
  );

  const handleQuestionSqlParametersChange = useCallback(
    (payload: { source: string; parameters: Record<string, any> }) => {
      // The initial event reflects Metabase defaults, not a user selection.
      if (payload.source !== "manual-change") return;
      persistActiveParameters(payload.parameters, { clearEmpty: true });
    },
    [persistActiveParameters],
  );

  const persistCurrentIframeParameters = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const liveUrl = iframe.contentWindow?.location?.href || iframe.src;
      persistActiveParameters(extractParametersFromUrl(liveUrl));
    } catch {
      persistActiveParameters(extractParametersFromUrl(iframe.src));
    }
  }, [persistActiveParameters]);

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;
    setMbEmbedParams(null);

    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      ...getAdminTenantHeader(),
    };

    if (email) headers["X-User-Email"] = email;

    (async () => {
      const query = new URLSearchParams();
      const reportId = activeResource?.reportId;
      if (reportId != null) query.set("report_id", String(reportId));
      const endpoint = `${API_BASE}/api/embed-params${query.size ? `?${query}` : ""}`;

      const r = await fetch(endpoint, {
        headers,
        cache: "no-store",
      });
      if (!r.ok) throw new Error("Failed to load embed params");
      const params: EmbedParams = await r.json();

      // A report can opt out of requiring airport without bypassing the
      // tenant's allowed airport values or other parameter lock rules.
      if (activeResource?.airportRequired === false) {
        params.requiredParameters = (params.requiredParameters || []).filter(
          (name) => name.toLowerCase() !== "airport",
        );
      }

      if (!cancelled) setMbEmbedParams(params);
    })().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [accessToken, email, menuReloadTick, activeResource]);

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;
    getAdministrationContext(accessToken, email || undefined)
      .then((context) => {
        if (cancelled) return;
        setAdministrationContext(context);
        setAdministrationContextError(null);
      })
      .catch((error) => {
        if (cancelled) return;
        // A 403 is expected for every non-Company Administrator. Keep the
        // administration entry hidden without disrupting the normal portal.
        setAdministrationContext(null);
        setAdministrationContextError(error?.message || "Unable to load administration access.");
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, email]);

  useEffect(() => {
    parameterSlugMapRef.current = {};
    knownParameterKeysRef.current = new Set();
    setRememberedParameterState(null);
    setLoadedFilterMemoryIdentity(null);

    if (!accessToken || !filterMemoryResourceKey || previewContext) {
      setLoadedFilterMemoryIdentity(filterMemoryIdentity);
      return;
    }

    let cancelled = false;

    getFilterPreference(accessToken, filterMemoryResourceKey, email)
      .then((preference) => {
        if (cancelled) return;

        const parameters = preference?.parameters;
        if (parameters && Object.keys(parameters).length) {
          const mappedParameters = filterToKnownParameters(
            mapParameterKeys(parameters, parameterSlugMapRef.current),
            knownParameterKeysRef.current,
          );
          const serverParameters = mappedParameters || parameters;
          const localMemory = readFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey);
          const nextParameters = preferRecentFilterParameters(localMemory, {
            parameters: serverParameters,
            updated_at: preference.updated_at,
          });
          if (!nextParameters) return;
          if (nextParameters === serverParameters) {
            writeFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey, {
              parameters: nextParameters,
            });
          }
          setRememberedParameterState({
            userKey: filterMemoryStorageUserKey,
            resourceKey: filterMemoryResourceKey,
            parameters: nextParameters,
          });
        }
      })
      .catch((error) => {
        if (error?.message?.startsWith("filter preference 404")) return;
        console.error(error);
      })
      .finally(() => {
        if (!cancelled) setLoadedFilterMemoryIdentity(filterMemoryIdentity);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, email, filterMemoryResourceKey, filterMemoryStorageUserKey, filterMemoryIdentity, previewContext]);

  useEffect(() => {
    const saveTimers = filterPreferenceSaveTimersRef.current;
    return () => {
      for (const { timer, save } of Array.from(saveTimers.values())) {
        window.clearTimeout(timer);
        save();
      }
    };
  }, []);

  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const mb = qs.get("mb");
    if (!mb) return;

    const targetUrl = decodeURIComponent(mb);
    setActiveItem({
      key: "mb-link",
      label: "",
      resource: { kind: "view", url: targetUrl },
    });

    const info = getEmbedResourceFromUrl(targetUrl);
    const publicDashboardUuid = getPublicDashboardUuid(targetUrl);

    if (accessToken && publicDashboardUuid) {
      const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
      if (email) headers["X-User-Email"] = email;
      fetch(`${API_BASE}/api/metabase-public-dashboard/${publicDashboardUuid}`, {
        headers,
        credentials: "include",
      })
        .then(async response => {
          if (!response.ok) throw new Error(`public dashboard ${response.status}`);
          return response.json();
        })
        .then(dashboard => {
          const dashboardId = Number(dashboard?.id);
          if (!Number.isInteger(dashboardId) || dashboardId <= 0) return;
          const slugMap = extractParameterSlugMap(dashboard);
          const knownKeys = extractParameterKeys(dashboard);
          const rawParameters = extractParametersFromUrl(targetUrl);
          const parameters = filterToKnownParameters(
            mapParameterKeys(rawParameters, slugMap),
            knownKeys,
            { dropEmpty: true },
          );
          const rawHidden = getClickthroughHiddenParameters(targetUrl, Object.keys(rawParameters || {}));
          const hiddenParameters = [...new Set(rawHidden
            .map(name => slugMap[name] || name)
            .filter(name => knownKeys.size === 0 || knownKeys.has(name)))];
          // The UUID resolver already returns the enriched dashboard payload.
          // Reuse it for tooltips instead of issuing the same public lookup a
          // second time after switching the renderer to the numeric SDK id.
          clickthroughColumnMetadataRef.current = {
            uuid: publicDashboardUuid,
            id: dashboardId,
            token: accessToken,
            email,
            scope: columnMetadataScope,
          };
          setColumnMetadata({
            id: dashboardId,
            token: accessToken,
            email,
            scope: columnMetadataScope,
            dashboard,
          });
          handleDashboardLoad(dashboard);
          setActiveItem(previous => previous?.key === "mb-link" ? {
            ...previous,
            label: dashboard.name || previous.label,
            resource: {
              kind: "sdk-dashboard",
              entityId: dashboardId,
              readOnly: true,
              publicDashboardUuid,
              initialParameters: parameters,
              hiddenParameters,
            },
          } : previous);
        })
        .catch(console.error);
    }

    if (!publicDashboardUuid && accessToken && info?.questionId) {
      fetchMetabaseName("question", info.questionId, accessToken, email).then((name) => {
        if (!name) return;
        setActiveItem((prev) =>
          prev?.key === "mb-link" ? { ...prev, label: name } : prev,
        );
      });
    } else if (!publicDashboardUuid && accessToken && info?.dashboardId) {
      fetchMetabaseName("dashboard", info.dashboardId, accessToken, email).then((name) => {
        if (!name) return;
        setActiveItem((prev) =>
          prev?.key === "mb-link" ? { ...prev, label: name } : prev,
        );
      });
    }

    qs.delete("mb");
    qs.delete("mbName");
    nav({ search: qs.toString() }, { replace: true });
// handleDashboardLoad is declared below this URL-promotion effect; it is only
// invoked asynchronously after the resolver returns.
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.search, nav, accessToken, email, columnMetadataScope]);

  useEffect(() => {
    const prev = document.title;
    const label = activeItem?.label ? ` – ${activeItem.label}` : "";
    document.title = `ARiVA Portal${label}`;
    return () => {
      document.title = prev;
    };
  }, [activeItem]);

  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;

    (async () => {
      const isCompanyAdministrator = administrationContext?.is_company_administrator === true;
      const [m, titles, savedData] = await Promise.all([
        fetchMenu(accessToken, email),
        fetchMenuTitles(accessToken, email).catch((error) => {
          console.error(error);
          return [];
        }),
        isCompanyAdministrator
          ? Promise.resolve(null)
          : listSavedItems(accessToken, undefined, email, "all").catch((error) => {
              console.error("Could not check saved report editing access:", error);
              return null;
            }),
      ]);
      if (!cancelled) setAvailableMenuTitles(titles);
      if (cancelled) return;

      let menuGroups: MenuGroup[] = (m || []) as MenuGroup[];

      // A Company Administrator's own tenant-role menu is the same published
      // menu every user with that role sees. Layer their personal Menu Title
      // choices on top the same way a Report Administration preview already
      // does, so picking a title here updates their own menu immediately
      // instead of only after publishing or opening a preview. This must run
      // on the raw (pre-normalizeMenuItem) resource shape: normalizing first
      // replaces a dashboard's resource.mbType/resourceId with an unrelated
      // shape, so the merge's de-dup-by-resource check can never recognize
      // an existing dashboard entry and ends up adding a second copy.
      if (administrationContext?.is_company_administrator) {
        try {
          const selections = await listPersonalMenuAssignments(accessToken, email);
          menuGroups = mergePersonalSelectionsIntoGroups(menuGroups, selections);
        } catch (error) {
          console.error("Could not add personal menu selections to menu:", error);
        }
      }
      if (cancelled) return;

      const editableSavedResources = editableSavedResourceKeys(savedData?.items || []);
      const normalized = menuGroups.map((g) => ({
        ...g,
        items: (g.items || []).map(item => normalizeMenuItem(item, isCompanyAdministrator, editableSavedResources)),
      }));

      setMenu(normalized);

      // A persisted preview is restored by the preview effect below. Do not
      // briefly mount the administrator's own default dashboard under the
      // preview tenant's Metabase identity while that menu is loading.
      if (getAdminPreviewContextKey() !== "none:none") return;

      const allItems = normalized.flatMap((g) => g.items || []);
      const defaultItem = allItems.find((it) => it.default) || allItems[0] || null;
      const nextDefaultKey = defaultItem?.key || null;
      const previousDefaultKey = lastDefaultMenuItemKeyRef.current;
      lastDefaultMenuItemKeyRef.current = nextDefaultKey;
      const shouldOpenDefault = openDefaultAfterMenuReloadRef.current;
      openDefaultAfterMenuReloadRef.current = false;
      const targetKey = menuTargetKeyAfterReloadRef.current;
      menuTargetKeyAfterReloadRef.current = null;

      setActiveItem((current) => {
        if (targetKey) {
          const targetItem = allItems.find((it) => it.key === targetKey);
          if (targetItem) return targetItem;
        }

        if (shouldOpenDefault && defaultItem) {
          return defaultItem;
        }

        if (
          defaultItem &&
          previousDefaultKey !== null &&
          nextDefaultKey !== previousDefaultKey
        ) {
          return defaultItem;
        }

        const selectedItem =
          (current ? allItems.find((it) => it.key === current.key) : null) ||
          defaultItem;

        return selectedItem;
      });
    })().catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [accessToken, email, menuReloadTick, administrationContext?.is_company_administrator]);

  useEffect(() => {
    if (!accessToken || previewContext) return;

    const [tenantValue, roleValue] = getAdminPreviewContextKey().split(":");
    const tenantId = Number(tenantValue);
    const roleId = Number(roleValue);
    if (!Number.isInteger(tenantId) || !Number.isInteger(roleId)) return;

    let cancelled = false;
    getReportPreview(accessToken, tenantId, roleId, email || undefined)
      .then((preview) => {
        if (cancelled) return;
        const previewMenu = (preview.menu || []).map((group: MenuGroup) => ({
          ...group,
          items: (group.items || []).map(item => normalizeMenuItem(item, true, new Set<string>())),
        }));
        const items = previewMenu.flatMap((group: MenuGroup) => group.items || []);
        setPreviewContext({ ...preview, menu: previewMenu });
        setActiveItem(items.find((item: MenuItem) => item.default) || items[0] || null);
      })
      .catch((error) => {
        console.error(error);
        clearAdminPreviewContext();
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, email, previewContext]);

  useEffect(() => {
    if (!accessToken || !activeResource) return;
    if (mode === "view" && !filterPreferenceReady) {
      setIframeSrc("");
      return;
    }

    const hasPublicUrl = Boolean(activeResource.url);
    const isLinkedClickthrough = activeItemKey === "mb-link";

    // Only block waiting for embed params when we do NOT already have a public URL
    if (mode === "view" && !hasPublicUrl && mbEmbedParams === null) return;

    if (mode !== "view") {
      setIframeSrc("");
      setIsLoading(false);
      setReady(true);
      return;
    }

    setIsLoading(true);
    setReady(false);

    const t = window.setTimeout(() => titleRef.current?.focus(), 0);

    if (hasPublicUrl) {
      const baseUrl = rewriteMetabaseUrlToProxy(activeResource.url);

      if (isLinkedClickthrough) {
        setIframeSrc(applyThemeToUrl(baseUrl, themeMode));
        window.clearTimeout(t);
        return;
      }

      const withParams = applyInitialParametersToUrl(
        baseUrl,
        mergedInitialParameters,
      );

      // const withRefresh = applyEmbedOptions(withParams, {
      //   refreshSec: refreshSec ?? undefined,
      // });

      const withRefresh = withParams;

      const finalUrl = applyThemeToUrl(
        applyRememberedParametersToUrl(
          withRefresh,
          rememberedParameters,
          mbEmbedParams?.initialParameters,
          {
            hiddenParameters: mbEmbedParams?.hiddenParameters,
            allowedValues: mbEmbedParams?.allowedValues,
            requiredParameters: mbEmbedParams?.requiredParameters,
          },
        ),
        themeMode,
      );

      setIframeSrc(finalUrl);
      window.clearTimeout(t);
      return;
    }

    getEmbedUrl(activeResource, accessToken, email, themeMode, mergedEmbedParams)
      .then((url) => {
        const withRemembered = applyRememberedParametersToUrl(
          url || "",
          rememberedParameters,
          mbEmbedParams?.initialParameters,
          {
            hiddenParameters: mbEmbedParams?.hiddenParameters,
            allowedValues: mbEmbedParams?.allowedValues,
            requiredParameters: mbEmbedParams?.requiredParameters,
          },
        );
        const withRefresh = applyEmbedOptions(withRemembered, {
          refreshSec: refreshSec ?? undefined,
        });
        setIframeSrc(applyThemeToUrl(withRefresh, themeMode));
      })
      .catch(console.error)
      .finally(() => window.clearTimeout(t));
  }, [
    activeItemKey,
    activeResource,
    accessToken,
    email,
    mode,
    filterPreferenceReady,
    refreshSec,
    themeMode,
    mbEmbedParams,
    mergedEmbedParams,
    mergedInitialParameters,
    rememberedParameters,
  ]);

  useEffect(() => {
    if (!filterMemoryResourceKey) return;
    if (!["view", "embedjs-dashboard"].includes(mode)) return;

    const onMessage = (event: MessageEvent) => {
      const parameters = extractParametersFromMessage(event.data);
      persistActiveParameters(parameters);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [filterMemoryResourceKey, mode, persistActiveParameters]);

  useEffect(() => {
    if (!filterMemoryResourceKey) return;
    if (!["sdk-dashboard", "embedjs-dashboard"].includes(mode)) return;
    if (typeof window.fetch !== "function") return;

    const isDashboardCreation = (url: string, method: string) =>
      isDashboardCreationRequest(url, method, MB_INSTANCE, window.location.href) ||
      isDashboardCreationRequest(
        url,
        method,
        process.env.REACT_APP_METABASE_INSTANCE_URL || window.location.origin,
        window.location.href,
      );
    const registerDashboard = (status: number, readBody: () => Promise<any>) =>
      registerCreatedDashboard(
        status,
        readBody,
        async (body: { kind: "dashboard"; metabase_id: number; title: string }) => {
          if (!accessToken) throw new Error("Missing access token; please sign in again.");
          await createSavedItem(accessToken, body, email ?? undefined);
          setQuestionSaveError(null);
        },
        (error: any) => setQuestionSaveError(
          "The dashboard was saved in Metabase, but could not be added to Saved Reports/Dashboards. " +
          (error?.message || "Registration failed."),
        ),
      );
    const originalFetch = window.fetch;
    const originalXhrOpen = window.XMLHttpRequest?.prototype.open;
    const originalXhrSend = window.XMLHttpRequest?.prototype.send;
    let lastSaved = "";

    const persistFromRequest = (url: string, body?: unknown) => {
      if (mode === "sdk-dashboard") return;
      if (
        /\/oauth2\/|\/api\/auth\/|\/token\b|\/login\b|\/session\b/i.test(url)
      ) {
        return;
      }

      if (knownParameterKeysRef.current.size === 0) return;

      const fromUrl = filterToKnownParameters(
        extractParametersFromUrl(url),
        knownParameterKeysRef.current,
        { dropEmpty: true },
      );
      if (fromUrl) {
        const serialized = JSON.stringify(fromUrl);
        if (serialized !== lastSaved) {
          lastSaved = serialized;
          persistActiveParameters(fromUrl);
        }
      }

      const explicitParameterArray = filterToKnownParameters(
        mapParameterKeys(
          extractParameterArrayFromRequestBody(body),
          parameterSlugMapRef.current,
        ),
        knownParameterKeysRef.current,
        { dropEmpty: false },
      );

      const fromBody = explicitParameterArray || filterToKnownParameters(
        mapParameterKeys(
          extractParametersFromRequestBody(body),
          parameterSlugMapRef.current,
        ),
        knownParameterKeysRef.current,
        { dropEmpty: true },
      );
      if (!fromBody) return;

      const serialized = JSON.stringify(fromBody);
      if (serialized === lastSaved) return;

      lastSaved = serialized;
      persistActiveParameters(fromBody, { clearEmpty: Boolean(explicitParameterArray) });
    };

    const persistFromFetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const request = input instanceof Request ? input : null;
        const url =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : request?.url || "";

        const body =
          init && Object.prototype.hasOwnProperty.call(init, "body")
            ? init.body
            : request
              ? await request.clone().text().catch(() => undefined)
              : undefined;
        persistFromRequest(url, body);
      } catch {
        // This is best-effort capture; the underlying SDK request should never be blocked.
      }
    };

    const wrappedFetch = ((input: RequestInfo | URL, init?: RequestInit) => {
      void persistFromFetch(input, init);
      const request = input instanceof Request ? input : null;
      const url = request?.url || String(input);
      const method = init?.method || request?.method || "GET";
      const response = originalFetch.call(window, input, init);
      if (!isDashboardCreation(url, method)) return response;
      return response.then(async (result) => {
        await registerDashboard(result.status, () => result.clone().json());
        return result;
      });
    }) as typeof window.fetch;
    window.fetch = wrappedFetch;

    if (originalXhrOpen && originalXhrSend) {
      window.XMLHttpRequest.prototype.open = function (
        method: string,
        url: string | URL,
        ...args: any[]
      ) {
        (this as any).__rtDashboardRequestMethod = method;
        (this as any).__rtFilterMemoryUrl =
          typeof url === "string" ? url : url?.toString?.() || "";
        return originalXhrOpen.call(this, method, url, ...args);
      };

      window.XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
        try {
          persistFromRequest(String((this as any).__rtFilterMemoryUrl || ""), body);
        } catch {
          // This is best-effort capture; the underlying SDK request should never be blocked.
        }
        if (isDashboardCreation(
          String((this as any).__rtFilterMemoryUrl || ""),
          String((this as any).__rtDashboardRequestMethod || ""),
        )) {
          this.addEventListener("load", () => {
            void registerDashboard(this.status, async () =>
              this.responseType === "json" ? this.response : JSON.parse(this.responseText),
            );
          }, { once: true });
        }
        return originalXhrSend.call(this, body);
      };
    }

    return () => {
      if (window.fetch === wrappedFetch) {
        window.fetch = originalFetch;
      }
      if (originalXhrOpen && window.XMLHttpRequest.prototype.open !== originalXhrOpen) {
        window.XMLHttpRequest.prototype.open = originalXhrOpen;
      }
      if (originalXhrSend && window.XMLHttpRequest.prototype.send !== originalXhrSend) {
        window.XMLHttpRequest.prototype.send = originalXhrSend;
      }
    };
  }, [filterMemoryResourceKey, mode, persistActiveParameters, accessToken, email]);

  useEffect(() => {
    if (!filterMemoryResourceKey) return;
    if (mode !== "embedjs-dashboard") return;

    let unsubscribe: (() => void) | undefined;
    let restoreDispatch: (() => void) | undefined;
    let lastSaved = "";
    let stopped = false;

    const saveFromStore = () => {
      const store = getMetabaseSdkStore();
      if (!store?.getState) return false;

      const parameters = extractParametersFromObject(store.getState());
      if (!parameters) return true;

      const mappedParameters = filterToKnownParameters(
        mapParameterKeys(parameters, parameterSlugMapRef.current),
        knownParameterKeysRef.current,
        { dropEmpty: true },
      );
      if (!mappedParameters) return true;

      const merged = mergeRememberedParameters(
        mergeNonEmptyParameters(
          readFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey)?.parameters,
          mappedParameters,
        ),
        mbEmbedParams?.initialParameters,
        {
          hiddenParameters: mbEmbedParams?.hiddenParameters,
          allowedValues: mbEmbedParams?.allowedValues,
          requiredParameters: mbEmbedParams?.requiredParameters,
        },
      );
      const serialized = JSON.stringify(merged);
      if (serialized === lastSaved) return true;

      lastSaved = serialized;
      persistActiveParameters(merged);
      return true;
    };

    const attach = () => {
      if (stopped || unsubscribe) return;

      const store = getMetabaseSdkStore();
      if (!store?.subscribe || !store?.getState) return;

      saveFromStore();
      unsubscribe = store.subscribe(saveFromStore);

      if (typeof store.dispatch === "function" && !restoreDispatch) {
        const originalDispatch = store.dispatch.bind(store);
        store.dispatch = (action: any) => {
          const parameters = extractParametersFromObject(action);
          if (parameters) {
            const mappedParameters = filterToKnownParameters(
              mapParameterKeys(parameters, parameterSlugMapRef.current),
              knownParameterKeysRef.current,
              { dropEmpty: true },
            );
            if (!mappedParameters) return originalDispatch(action);

            const merged = mergeRememberedParameters(
              mergeNonEmptyParameters(
                readFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey)?.parameters,
                mappedParameters,
              ),
              mbEmbedParams?.initialParameters,
              {
                hiddenParameters: mbEmbedParams?.hiddenParameters,
                allowedValues: mbEmbedParams?.allowedValues,
                requiredParameters: mbEmbedParams?.requiredParameters,
              },
            );
            persistActiveParameters(merged);
          }

          return originalDispatch(action);
        };

        restoreDispatch = () => {
          store.dispatch = originalDispatch;
        };
      }
    };

    attach();
    const id = window.setInterval(attach, 500);

    return () => {
      stopped = true;
      window.clearInterval(id);
      unsubscribe?.();
      restoreDispatch?.();
    };
  }, [
    filterMemoryResourceKey,
    filterMemoryStorageUserKey,
    mbEmbedParams?.initialParameters,
    mbEmbedParams?.hiddenParameters,
    mbEmbedParams?.allowedValues,
    mbEmbedParams?.requiredParameters,
    mode,
    persistActiveParameters,
  ]);

  useEffect(() => {
    if (mode !== "view" || !iframeSrc) return;

    persistCurrentIframeParameters();

    const id = window.setInterval(() => {
      persistCurrentIframeParameters();
    }, 1000);

    return () => window.clearInterval(id);
  }, [iframeSrc, mode, persistCurrentIframeParameters]);

  // const [, setSdkRefreshTick] = useState(0);

  // useEffect(() => {
  //   if (mode !== "sdk-dashboard") return;
  //   if (!entityIdNum) return;
  //   if (!refreshSec || refreshSec <= 0) return;

  //   const id = window.setInterval(() => {
  //     setSdkRefreshTick((t) => t + 1);
  //   }, refreshSec * 1000);

  //   return () => window.clearInterval(id);
  // }, [mode, entityIdNum, refreshSec]);

  // const [, setTick] = useState(0);

  // useEffect(() => {
  //   if (!refreshSec || refreshSec <= 0) return;
  //   const t = window.setInterval(() => setTick((x) => x + 1), refreshSec * 1000);
  //   return () => window.clearInterval(t);
  // }, [refreshSec, entityIdNum]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    persistCurrentIframeParameters();
    const prefersNoMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (prefersNoMotion) setReady(true);
    else requestAnimationFrame(() => setReady(true));
  };

  const handleSignOut = async () => {
    // Clear app/browser cache
    localStorage.clear();
    sessionStorage.clear();

    // Clear possible cached SDK data
    window.dispatchEvent(new Event("storage"));

    // Optional: clear service worker caches
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }

    const clientId = COGNITO_CLIENT_ID || "";
    const logoutUri =
      process.env.REACT_APP_COGNITO_LOGOUT_REDIRECT_URI || "";

    const url =
      `${COGNITO_DOMAIN}/logout` +
      `?client_id=${encodeURIComponent(clientId)}` +
      `&logout_uri=${encodeURIComponent(logoutUri)}`;

    window.location.replace(url);
  };

  const missingToken = !accessToken;

  const isCreate = mode === "create-report" || mode === "create-dashboard";
  const isSavedList = mode === "saved";
  const showTitlebar = !isCreate && !isSavedList && mode !== "report-administration";

  const handleMenuQueryChange = useCallback((value: string) => {
    setMenuQuery(value);
  }, []);

  const handleSelectMenuItem = useCallback(
    (groupTitle: string, item: MenuItem) => {
      if (activeItem?.key === item.key) {
        setIsLoading(true);
        setReady(false);
        setReloadTick((t) => t + 1);
      } else {
        setActiveItem(item);
      }
    },
    [activeItem?.key],
  );

  // Bypasses the same-key "reload instead of replace" logic above: the
  // Model/SQL choices share the "create-report" key, so switching between
  // them while already on the Create Report page must always replace the
  // active item rather than just reload the one that's already active.
  const handleSelectCreateReportSource = useCallback(
    (sourceMode: "model" | "sql") => {
      setActiveItem({
        key: "create-report",
        label: "Create Report",
        resource: { kind: "create-report", sourceMode },
      });
    },
    [],
  );

  const handleSetDefaultMenuItem = useCallback(async (key: string) => {
    if (!accessToken || !key) return;

    try {
      await setDefaultMenuItem(accessToken, key, email || undefined);
      setMenuReloadTick((v) => v + 1);
    } catch (e: any) {
      window.alert(e?.message || "Failed to set default");
    }
  }, [accessToken, email]);

  const handleClearDefaultMenuItem = useCallback(async () => {
    if (!accessToken) return;

    try {
      await clearDefaultMenuItem(accessToken, email || undefined);
      setMenuReloadTick((v) => v + 1);
    } catch (e: any) {
      window.alert(e?.message || "Failed to clear default");
    }
  }, [accessToken, email]);

  const handleSavedMenuChanged = useCallback(async (options?: {
    openDefault?: boolean;
    targetKey?: string;
    publishedReport?: {
      id?: number;
      slug: string;
      title: string;
      mb_type: "card" | "dashboard";
      mb_resource_id: number;
      menu_section?: string | null;
      refresh_sec?: number | null;
      public_url?: string | null;
      airport_required?: boolean;
    };
    preview?: {
      tenant: { id: number; code: string; name: string };
      role: { id: number; code: string; name: string };
      menu: any[];
    } | null;
  }) => {
    if (previewContext && accessToken) {
      const previewTenantId = previewContext.tenant.id;
      const previewRoleId = previewContext.role.id;

      // The publish response is generated after its transaction commits, so
      // prefer it over a follow-up GET for the currently selected preview.
      if (
        options?.preview &&
        options.preview.tenant.id === previewTenantId &&
        options.preview.role.id === previewRoleId
      ) {
        const committedPreview = options.preview as ReportPreview;
        const nextMenu = (committedPreview.menu || []).map((group: MenuGroup) => ({
          ...group,
          items: (group.items || []).map(item => normalizeMenuItem(item, true, new Set<string>())),
        }));
        setPreviewContext((current) => {
          if (
            !current ||
            current.tenant.id !== previewTenantId ||
            current.role.id !== previewRoleId
          ) {
            return current;
          }
          return { ...current, menu: nextMenu };
        });
        return;
      }

      // Publishing already succeeded at this point. Add the returned report to
      // the visible preview immediately so the sidebar never waits for a
      // second request (or a browser refresh) to reflect the action.
      if (options?.publishedReport) {
        const report = options.publishedReport;
        const sectionTitle = String(report.menu_section || "REPORTS").trim() || "REPORTS";
        const item = normalizeMenuItem({
          key: report.slug,
          label: report.title,
          resource: {
            mbType: report.mb_type,
            resourceId: report.mb_resource_id,
            refreshSec: report.refresh_sec ?? null,
            canEdit: true,
            publicUrl: report.public_url ?? null,
            reportId: report.id ?? null,
            airportRequired: report.airport_required ?? true,
          },
        }, true, new Set<string>());

        setPreviewContext((current) => {
          if (
            !current ||
            current.tenant.id !== previewTenantId ||
            current.role.id !== previewRoleId
          ) {
            return current;
          }

          const withoutPublishedItem = current.menu
            .map((group) => ({
              ...group,
              items: (group.items || []).filter((menuItem) => menuItem.key !== item.key),
            }))
            .filter((group) => group.items.length > 0 || group.title.toUpperCase() === "MY DATA" || group.title.toUpperCase() === "CREATE");
          const matchingIndex = withoutPublishedItem.findIndex(
            (group) => group.title.trim().toUpperCase() === sectionTitle.toUpperCase(),
          );

          if (matchingIndex >= 0) {
            withoutPublishedItem[matchingIndex] = {
              ...withoutPublishedItem[matchingIndex],
              items: [...withoutPublishedItem[matchingIndex].items, item],
            };
          } else {
            const createIndex = withoutPublishedItem.findIndex(
              (group) => group.title.toUpperCase() === "CREATE",
            );
            const group = { title: sectionTitle, items: [item] };
            if (createIndex >= 0) withoutPublishedItem.splice(createIndex, 0, group);
            else withoutPublishedItem.push(group);
          }

          return { ...current, menu: withoutPublishedItem };
        });
      }

      try {
        const preview = await getReportPreview(
          accessToken,
          previewTenantId,
          previewRoleId,
          email || undefined,
        );
        const nextMenu = (preview.menu || []).map((group: MenuGroup) => ({
          ...group,
          items: (group.items || []).map(item => normalizeMenuItem(item, true, new Set<string>())),
        }));
        setPreviewContext((current) => {
          if (
            !current ||
            current.tenant.id !== previewTenantId ||
            current.role.id !== previewRoleId
          ) {
            return current;
          }
          return { ...preview, menu: nextMenu };
        });
        if (options?.openDefault) {
          const items = nextMenu.flatMap((group: MenuGroup) => group.items || []);
          const target = options.targetKey
            ? items.find((item: MenuItem) => item.key === options.targetKey)
            : items.find((item: MenuItem) => item.default);
          if (target) setActiveItem(target);
        }
      } catch (error) {
        console.error(error);
      }
      return;
    }

    openDefaultAfterMenuReloadRef.current = !!options?.openDefault;
    menuTargetKeyAfterReloadRef.current = options?.targetKey || null;
    setMenuReloadTick((v) => v + 1);
  }, [accessToken, email, previewContext]);

  const handlePreview = useCallback((preview: ReportPreview) => {
    setAdminPreviewContext(preview.tenant.id, preview.role.id);
    // The Metabase SDK keeps a process-wide store. Starting the preview from a
    // clean page makes it identical to a tenant login instead of reusing the
    // administrator tenant's already-initialized SDK state.
    window.location.reload();
  }, []);

  const handleExitPreview = useCallback(() => {
    clearAdminPreviewContext();
    window.location.reload();
  }, []);

  const previewTenantId = previewContext?.tenant.id;
  const previewRoleId = previewContext?.role.id;

  const handleDashboardLoad = useCallback((dashboard: any) => {
    const nextMap = extractParameterSlugMap(dashboard);
    parameterSlugMapRef.current = {
      ...parameterSlugMapRef.current,
      ...nextMap,
    };

    const nextKeys = extractParameterKeys(dashboard);
    knownParameterKeysRef.current = new Set([
      ...Array.from(knownParameterKeysRef.current),
      ...Array.from(nextKeys),
      ...Object.values(parameterSlugMapRef.current),
    ]);

    if (!filterMemoryResourceKey) return;

    const currentParameters =
      readFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey)?.parameters;
    const cleanedParameters = filterToKnownParameters(
      mapParameterKeys(currentParameters, parameterSlugMapRef.current),
      knownParameterKeysRef.current,
      { dropEmpty: false },
    );

    if (!cleanedParameters) return;

    const currentSerialized = JSON.stringify(currentParameters || {});
    const cleanedSerialized = JSON.stringify(cleanedParameters);
    if (currentSerialized === cleanedSerialized) return;

    writeFilterMemory(filterMemoryStorageUserKey, filterMemoryResourceKey, {
      parameters: cleanedParameters,
    });
    setRememberedParameterState({
      userKey: filterMemoryStorageUserKey,
      resourceKey: filterMemoryResourceKey,
      parameters: cleanedParameters,
    });
  }, [filterMemoryResourceKey, filterMemoryStorageUserKey]);

  useEffect(() => {
    if (!accessToken) return;
    if (mode !== "sdk-dashboard") return;
    if (!entityIdNum) return;

    const publicDashboardUuid = typeof resource.publicDashboardUuid === "string"
      ? resource.publicDashboardUuid : null;
    const clickthroughMetadata = clickthroughColumnMetadataRef.current;
    if (
      publicDashboardUuid &&
      clickthroughMetadata?.uuid === publicDashboardUuid &&
      clickthroughMetadata.id === entityIdNum &&
      clickthroughMetadata.token === accessToken &&
      clickthroughMetadata.email === email &&
      clickthroughMetadata.scope === columnMetadataScope
    ) {
      return;
    }

    let cancelled = false;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
    };
    if (email) headers["X-User-Email"] = email;

    if (previewTenantId && previewRoleId) {
      headers["X-Reporttool-Admin-Tenant"] = String(previewTenantId);
      headers["X-Reporttool-Admin-Role"] = String(previewRoleId);
    }
    setColumnMetadata(null);
    const metadataUrl = publicDashboardUuid
      ? `${API_BASE}/api/metabase-public-dashboard/${encodeURIComponent(publicDashboardUuid)}`
      : `${API_BASE}/api/metabase-dashboard/${entityIdNum}`;
    fetch(metadataUrl, {
      headers,
      credentials: "include",
    })
      .then(async (resp) => {
        if (!resp.ok) {
          const text = await resp.text().catch(() => "");
          throw new Error(`dashboard metadata ${resp.status}: ${text}`);
        }
        return resp.json();
      })
      .then((dashboard) => {
        if (!cancelled) {
          setColumnMetadata({ id: entityIdNum, token: accessToken, email, scope: columnMetadataScope, dashboard });
          handleDashboardLoad(dashboard);
        }
      })
      .catch(console.error);

    return () => {
      cancelled = true;
    };
  }, [accessToken, email, entityIdNum, handleDashboardLoad, mode, reloadTick, columnMetadataScope, previewTenantId, previewRoleId, resource.publicDashboardUuid]);

  return renderWithMetabase(
    mode === "create-report" || mode === "create-dashboard" ? "creator" : "runtime",
    (
    <MantineProvider forceColorScheme={themeMode}>
      <DevSdkOverlaySuppressor />
      <div ref={shellRef} className={shellClassName} data-theme={themeMode}>
        <header className="rt-header">
          <button
            className="rt-burger"
            aria-label="Toggle menu"
            onClick={() => setCollapsed((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>

          <img
            className="rt-brand-logo"
            src={logoSrc || "/ReportToolLogo.png"}
            alt="ARiVA Report Tool"
          />

          <div className="rt-header-spacer" />

          <div className="rt-user-menu-wrapper">
            <div className="rt-user-gear">
              <button
                type="button"
                className="rt-gear"
                aria-label="User menu"
                aria-haspopup="true"
                aria-expanded={showUserMenu ? "true" : "false"}
                onClick={() => setShowUserMenu((v) => !v)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19.14,12.94a7.43,7.43,0,0,0,.05-.94,7.43,7.43,0,0,0-.05-.94l2.11-1.65a.5.5,0,0,0,.12-.64l-2-3.46a.5.5,0,0,0-.6-.22l-2.49,1a7.51,7.51,0,0,0-1.63-.94L14.5,2.5a.5.5,0,0,0-.5-.5H10a.5.5,0,0,0-.5.5l-.25,2.59a7.51,7.51,0,0,0-1.63.94l-2.49-1a.5.5,0,0,0-.6.22l-2,3.46a.5.5,0,0,0,.12.64L3.76,11.06a7.43,7.43,0,0,0-.05.94,7.43,7.43,0,0,0,.05.94L1.65,14.59a.5.5,0,0,0-.12.64l2,3.46a.5.5,0,0,0,.6.22l2.49-1a7.51,7.51,0,0,0,1.63.94L9.5,21.5a.5.5,0,0,0,.5.5h4a.5.5,0,0,0,.5-.5l.25-2.59a7.51,7.51,0,0,0,1.63-.94l2.49,1a.5.5,0,0,0,.6-.22l2-3.46a.5.5,0,0,0-.12-.64ZM12,15.5A3.5,3.5,0,1,1,15.5,12,3.5,3.5,0,0,1,12,15.5Z" />
                </svg>
              </button>
            </div>

            <button
              type="button"
              className="rt-gear rt-theme-toggle"
              onClick={() => setThemeMode(themeMode === "dark" ? "light" : "dark")}
              aria-label="Toggle theme"
              title="Toggle theme"
            >
              {themeMode === "dark" ? (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M6.76 4.84l-1.8-1.79-1.41 1.41 1.79 1.8 1.42-1.42ZM1 11h3v2H1v-2Zm10-10h2v3h-2V1Zm9.66 3.46-1.41-1.41-1.8 1.79 1.42 1.42 1.79-1.8ZM20 11h3v2h-3v-2ZM11 20h2v3h-2v-3Zm6.24-1.08 1.8 1.79 1.41-1.41-1.79-1.8-1.42 1.42ZM4.22 19.95l1.41 1.41 1.8-1.79-1.42-1.42-1.79 1.8ZM12 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12Z"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M21.64 13.65A8.01 8.01 0 0 1 10.35 2.36a.75.75 0 0 0-.93-.93A10 10 0 1 0 22.57 14.58a.75.75 0 0 0-.93-.93Z"
                  />
                </svg>
              )}
            </button>

            {showUserMenu && (
              <div className="rt-user-menu" role="menu">
                <button
                  type="button"
                  className="rt-user-menu-item"
                  onClick={handleSignOut}
                  role="menuitem"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <ReportToolSidebar
          collapsed={collapsed}
          menuQuery={menuQuery}
          filteredMenu={filteredMenu}
          activeItemKey={activeItem?.key}
          onMenuQueryChange={handleMenuQueryChange}
          onSelectItem={handleSelectMenuItem}
          canCreateSql={administrationContext?.is_company_administrator === true}
          onSelectCreateReportSource={handleSelectCreateReportSource}
        />


        <main className="rt-main">
          {previewContext && mode !== "report-administration" && (
            <div className="rt-preview-bar" role="status">
              <div>
                <span className="rt-preview-label">Previewing</span>
                <strong>{previewContext.tenant.code} · {previewContext.role.name}</strong>
                <span>Tenant-role menu preview</span>
              </div>
              <button type="button" onClick={handleExitPreview}>
                Exit preview
              </button>
            </div>
          )}
          {showTitlebar && (
              <div className="rt-titlebar">
                <h1 className="rt-crumbs" tabIndex={-1} ref={titleRef}>
                  {activeItem?.label || ""}
                </h1>
              </div>
          )}

          <section
            // key={`${activeItem?.key || "none"}-${reloadTick}`}
            className={[
              "rt-stage",
              mode === "view" && "is-view",
              isEditorMode && "is-editor",
              mode === "report-administration" && "is-report-administration",
              mode === "sdk-dashboard" && "is-dashboard",
              mode === "sdk-dashboard" && readOnly && "is-dashboard-readonly",
              mode === "sdk-dashboard" && !readOnly && "is-dashboard-editable",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{ minHeight: 0 }}
          >
            {missingToken ? (
              <div style={{ padding: 16 }}>
                Missing access token; please sign in again.
              </div>
            ) : (
              <>
                {mode === "view" && (
                  <>
                    {isLoading && (
                      <div className="rt-overlay" role="status" aria-label="Loading">
                        <div className="rt-spinner" />
                        <div className="rt-overlay-text">Updating…</div>
                      </div>
                    )}

                    {iframeSrc && (
                      <iframe
                        ref={iframeRef}
                        key={`${iframeKey}-${reloadTick}`}
                        title={activeItem?.label || "Report"}
                        src={iframeSrc}
                        className={`rt-iframe ${ready ? "is-ready" : ""}`}
                        onLoad={handleIframeLoad}
                      />
                    )}
                  </>
                )}

                {mode !== "view" && (
                  <div className="rt-content-shell">
                    <div className="rt-content-inner">
                      {questionSaveError && (
                        <div role="alert" style={{ padding: 8, color: "#ff6b6b" }}>
                          {questionSaveError}
                        </div>
                      )}
                      {mode === "embedjs-dashboard" && filterPreferenceReady &&
                        entityIdNum !== null &&
                        !missingToken && (
                          <SdkErrorBoundary>
                            <EmbedJsDashboard
                              apiBase={API_BASE}
                              accessToken={accessToken!}
                              email={email}
                              instanceUrl={MB_INSTANCE}
                              dashboardId={entityIdNum}
                              themeMode={themeMode}
                              themeHostEl={shellRef.current}
                              refreshSec={refreshSec}
                              initialParameters={mergedInitialParameters}
                            />
                          </SdkErrorBoundary>
                        )}

                      {mode === "create-report" && (
                        <div className="rt-create-report-page">
                          <SdkErrorBoundary>
                            <CreateReport sourceMode={resource.sourceMode === "sql" ? "sql" : "model"} />
                          </SdkErrorBoundary>
                        </div>
                      )}

                      {mode === "create-dashboard" && (
                        <div className="rt-create-report-page">
                          <SdkErrorBoundary>
                            <CreateDashboard
                              onClose={() =>
                                setActiveItem({
                                  key: "saved",
                                  label: "Saved Reports/Dashboards",
                                  resource: { kind: "saved" },
                                })
                              }
                              onCreated={(entityId) =>
                                setActiveItem({
                                  key: `saved-dashboard-${entityId}`,
                                  label: "Saved Dashboard",
                                  resource: {
                                    kind: "sdk-dashboard",
                                    entityId,
                                    readOnly: false,
                                    canEdit: true,
                                  },
                                })
                              }
                            />
                          </SdkErrorBoundary>
                        </div>
                      )}

                      {mode === "saved" && (
                        <MyDataSavedBrowser
                          apiBase={API_BASE}
                          accessToken={accessToken}
                          email={email}
                          onPublished={handleSavedMenuChanged}
                          menuTitles={savedMenuTitles}
                          defaultMenuItemKey={defaultMenuItemKey}
                          onSetDefault={handleSetDefaultMenuItem}
                          onClearDefault={handleClearDefaultMenuItem}
                          administrativeTenantLabel={
                            previewContext
                              ? `${previewContext.tenant.code} — ${previewContext.tenant.name}`
                              : null
                          }
                          onExitAdministrativeTenant={handleExitPreview}
                          onNewQuestion={(sourceMode) =>
                            setActiveItem({
                              key: "create-report",
                              label: "Create Report",
                              resource: { kind: "create-report", sourceMode },
                            })
                          }
                          canCreateSql={administrationContext?.is_company_administrator === true}
                          onNewDashboard={() =>
                            setActiveItem({
                              key: "create-dashboard",
                              label: "Create Dashboard",
                              resource: { kind: "create-dashboard" },
                            })
                          }
                          onOpen={({ id, kind, metabase_id, title, can_edit_saved }) => {
                            const entityId = Number(metabase_id);
                            if (!Number.isInteger(entityId)) return;

                            setActiveItem({
                              key: `saved-${kind}-${id}-${entityId}`,
                              label:
                                title || (kind === "card" ? "Saved Report" : "Saved Dashboard"),
                              resource: {
                                kind: kind === "card" ? "sdk-question" : "sdk-dashboard",
                                entityId,
                                readOnly: can_edit_saved !== true,
                                canEdit: kind === "dashboard" ? can_edit_saved === true : undefined,
                              },
                            });
                          }}
                        />
                      )}

                      {mode === "report-administration" && (
                        <ReportAdministration
                          accessToken={accessToken}
                          email={email}
                          context={administrationContext}
                          contextError={administrationContextError}
                          onPreview={handlePreview}
                          onCreateReport={(sourceMode) =>
                            setActiveItem({
                              key: "create-report",
                              label: "Create Report",
                              resource: { kind: "create-report", sourceMode },
                            })
                          }
                          onCreateDashboard={() =>
                            setActiveItem({
                              key: "create-dashboard",
                              label: "Create Dashboard",
                              resource: { kind: "create-dashboard" },
                            })
                          }
                        />
                      )}

                      {mode === "sdk-question" && entityIdNum != null && filterPreferenceReady && (
                        <SdkErrorBoundary>
                          {!readOnly ? (
                            <InteractiveQuestion
                              key={`${entityIdNum}-${readOnly ? "readonly" : "editable"}`}
                              questionId={entityIdNum}
                              height="85vh"
                              title={false}
                              sqlParameters={mergedInitialParameters}
                              onSqlParametersChange={handleQuestionSqlParametersChange}
                              hiddenParameters={mbEmbedParams?.hiddenParameters}
                              withDownloads
                              withChartTypeSelector
                              withEditorButton
                              entityTypes={["model", "table", "question"]}
                              dataPicker="staged"
                              onSave={handleSavedQuestionSave}
                              isSaveEnabled
                            />
                          ) : (
                            <StaticQuestion
                              key={`${entityIdNum}-readonly`}
                              questionId={entityIdNum}
                              height="100%"
                              title={false}
                              sqlParameters={mergedInitialParameters}
                              onSqlParametersChange={handleQuestionSqlParametersChange}
                              hiddenParameters={mbEmbedParams?.hiddenParameters}
                              withDownloads
                            >
                              <div className="rt-readonly-question">
                                <div className="rt-readonly-question-toolbar">
                                  <StaticQuestion.SqlParametersList />
                                  <StaticQuestion.DownloadWidgetDropdown />
                                </div>
                                <StaticQuestion.QuestionVisualization height="calc(100% - 48px)" />
                              </div>
                            </StaticQuestion>
                          )}
                        </SdkErrorBoundary>
                      )}

                      {mode === "sdk-dashboard" && entityIdNum !== null && filterPreferenceReady && (
                        <SdkErrorBoundary>
                          <SdkDashboardView
                            key={`${entityIdNum}-${readOnly ? "readonly" : "editable"}-${reloadTick}`}
                            entityId={entityIdNum}
                            refreshSec={refreshSec}
                            readOnly={readOnly}
                            withTitle={!showTitlebar}
                            columnMetadata={columnMetadata?.id === entityIdNum && columnMetadata.token === accessToken && columnMetadata.email === email && columnMetadata.scope === columnMetadataScope ? columnMetadata.dashboard : null}
                            embedParams={mergedEmbedParams}
                            onParametersChange={handleSdkParametersChange}
                            onQuestionSave={handleSavedQuestionSave}
                          />
                        </SdkErrorBoundary>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        </main>
      </div>
    </MantineProvider>
    ),
  );
}
